import type { AccountSettings } from "./accounts";

/**
 * Diễn giải cấu hình thành nhịp gửi thực tế.
 *
 * Phải khớp với `nextSendDelayMs` ở server: nhịp = đúng khoảng nghỉ người dùng
 * đặt, không gì khác. `maxPerHour`/`maxPerDay` là TRẦN — chạm thì hoãn tới đầu
 * giờ (hoặc ngày) sau, nên chúng giới hạn SẢN LƯỢNG chứ không đổi nhịp.
 */
export function describePacing(settings: AccountSettings) {
    const intervalMs = (settings.minDelayMs + settings.maxDelayMs) / 2;

    // Nhịp này chạy hết một giờ thì ra bao nhiêu tin, nếu không có trần
    const uncappedPerHour = Math.floor(3_600_000 / intervalMs);
    const perHour = Math.min(settings.maxPerHour, uncappedPerHour);

    const activeHours = activeWindowHours(settings.activeFrom, settings.activeTo);
    const perDay = Math.min(settings.maxPerDay, Math.floor(perHour * activeHours));

    return {
        intervalMs,
        perHour,
        perDay,
        activeHours,
        /**
         * Trần giờ có cắt bớt sản lượng không. Cắt = gửi hết trần trong
         * `burstMinutes` phút rồi đứng im chờ sang giờ mới.
         */
        cappedByHour: settings.maxPerHour < uncappedPerHour,
        burstMinutes: Math.round((settings.maxPerHour * intervalMs) / 60_000),
    };
}

function activeWindowHours(from: string, to: string): number {
    const minutes = (value: string) => {
        const [h = "0", m = "0"] = value.split(":");
        return Number(h) * 60 + Number(m);
    };

    const span = (minutes(to) - minutes(from) + 24 * 60) % (24 * 60);
    return (span === 0 ? 24 * 60 : span) / 60;
}

export function formatInterval(ms: number): string {
    const seconds = Math.round(ms / 1000);
    if (seconds < 60) return `${seconds} giây`;

    const minutes = Math.floor(seconds / 60);
    const rest = seconds % 60;
    return rest === 0 ? `${minutes} phút` : `${minutes} phút ${rest} giây`;
}

export function formatDuration(ms: number): string {
    const minutes = Math.round(ms / 60_000);
    if (minutes < 60) return `${minutes} phút`;

    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours} giờ` : `${hours} giờ ${rest} phút`;
}

/**
 * Thời gian gửi hết `count` tin trong MỘT ngày làm việc.
 *
 * Trần giờ làm nhịp thành từng đợt chứ không đều: bắn hết trần trong
 * `trần × nghỉ` ms rồi đứng im tới đầu giờ sau. Nhân thẳng `count × nghỉ` sẽ
 * bỏ qua toàn bộ những quãng đứng im đó và báo nhanh hơn thực tế rất nhiều.
 */
function msForCount(count: number, perHour: number, intervalMs: number, activeHours: number): number {
    const fullHours = Math.floor(count / Math.max(1, perHour));
    const rest = count - fullHours * Math.max(1, perHour);
    return Math.min(fullHours * 3_600_000 + rest * intervalMs, activeHours * 3_600_000);
}

/** Thời gian ước tính để gửi hết `groupCount` nhóm, tính cả lúc phải chờ qua đêm. */
export function estimateCampaignDuration(settings: AccountSettings, groupCount: number): number {
    const { perDay, perHour, intervalMs, activeHours } = describePacing(settings);
    if (groupCount <= 0) return 0;

    const daily = Math.max(1, perDay);
    const days = Math.ceil(groupCount / daily);
    const lastDayCount = groupCount - (days - 1) * daily;

    // Các ngày trước ngày cuối tính trọn 24h (gồm cả khoảng nghỉ ngoài khung giờ).
    // Ngày cuối chỉ tính đúng số tin còn lại — số nhóm vừa khít hạn mức ngày
    // KHÔNG được tính thành trọn một ngày.
    return (days - 1) * 86_400_000 + msForCount(lastDayCount, perHour, intervalMs, activeHours);
}
